// static/table_engine_src/ui/popovers.js
"use strict";

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

// Explicit width/height so it shows even if CSS doesn't size it.
const CLOSE_ICON_SVG = `
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"
       fill="none" stroke="currentColor" stroke-width="2"
       stroke-linecap="round" stroke-linejoin="round"
       style="display:block;">
    <path d="M18 6L6 18"></path>
    <path d="M6 6l12 12"></path>
  </svg>
`.trim();

function _isOnlyCloseGlyph(el) {
  if (!el) return false;

  // If author already provided SVG, it’s fine.
  if (el.querySelector && el.querySelector("svg")) return false;

  const t = String(el.textContent || "").trim();
  if (t === "×" || t === "✕" || t === "✖" || t === "x" || t === "X") return true;

  const h = String(el.innerHTML || "").trim().toLowerCase();
  if (h === "&times;" || h === "&#215;" || h === "&#x00d7;") return true;

  return false;
}

function ensureCloseIcon(el) {
  if (!el || el.nodeType !== 1) return;

  if (!el.getAttribute("data-ui-pop-close")) el.setAttribute("data-ui-pop-close", "1");

  if (el.querySelector && el.querySelector("svg")) {
    if (!el.getAttribute("aria-label")) el.setAttribute("aria-label", "Close");
    if (!el.getAttribute("title")) el.setAttribute("title", "Close");
    return;
  }

  const html = (el.innerHTML || "").trim();

  if (!html || html === "&nbsp;") {
    el.innerHTML = CLOSE_ICON_SVG;
  } else if (_isOnlyCloseGlyph(el)) {
    el.innerHTML = CLOSE_ICON_SVG;
  } else {
    // Respect author-provided content; do NOT append.
  }

  if (!el.getAttribute("aria-label")) el.setAttribute("aria-label", "Close");
  if (!el.getAttribute("title")) el.setAttribute("title", "Close");
}

function patchCloseButtons(root = document) {
  if (!root || !root.querySelectorAll) return;
  const nodes = root.querySelectorAll(".ui-pop-close, [data-ui-pop-close], [data-ui-pop-close='1']");
  nodes.forEach(ensureCloseIcon);
}

function _readPlacementHint(popEl, anchorEl) {
  const p1 = String(popEl?.dataset?.uiPopPlacement || "").trim().toLowerCase();
  if (p1 === "top" || p1 === "bottom") return p1;
  const p2 = String(anchorEl?.dataset?.uiPopPlacement || "").trim().toLowerCase();
  if (p2 === "top" || p2 === "bottom") return p2;
  return ""; // auto
}

function positionPopover(el, anchorEl) {
  const pad = 10;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Ensure measurable
  el.style.left = "0px";
  el.style.top = "0px";

  const pr = el.getBoundingClientRect();
  const ar = anchorEl?.getBoundingClientRect?.();

  // Keep within viewport height; internal scroll via CSS.
  const maxH = Math.max(120, vh - pad * 2);
  if (pr.height > maxH) el.style.maxHeight = `${Math.floor(maxH)}px`;
  else el.style.maxHeight = "";

  // Re-measure
  const pr2 = el.getBoundingClientRect();
  const popW = pr2.width;
  const popH = pr2.height;

  const baseLeft = ar ? ar.left : (vw - popW) / 2;
  let left = clamp(baseLeft, pad, vw - popW - pad);

  const placement = _readPlacementHint(el, anchorEl);
  const hasDateInput = !!(el.querySelector && el.querySelector("input[type='date']"));
  const extraBelow = hasDateInput ? 360 : 0;

  let top;

  if (!ar) {
    top = clamp((vh - popH) / 2, pad, vh - popH - pad);
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
    return;
  }

  const wantTop = placement === "top";
  const wantBottom = placement === "bottom";

  const bottomCandidate = ar.bottom + 8;
  const topCandidate = ar.top - popH - 8;

  const bottomFits = (bottomCandidate + popH + extraBelow + pad) <= vh;
  const topFits = (topCandidate - pad) >= 0;

  if (wantTop) {
    top = topCandidate;
    if (!topFits) top = bottomCandidate;
  } else if (wantBottom) {
    top = bottomCandidate;
    if (!bottomFits) top = topCandidate;
  } else {
    top = bottomCandidate;
    if (!bottomFits) top = topCandidate;
  }

  top = clamp(top, pad, vh - popH - pad);

  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(top)}px`;
}

const __uiPopInstances = new Set();
let __uiPopGlobalsInstalled = false;

function installPopoverGlobalsOnce() {
  if (__uiPopGlobalsInstalled) return;
  __uiPopGlobalsInstalled = true;

  document.addEventListener(
    "pointerdown",
    (e) => {
      const target = e.target;
      for (const inst of [...__uiPopInstances]) {
        if (!inst?.isOpen?.()) continue;
        if (inst.el?.contains?.(target)) continue;
        inst.close?.();
      }
    },
    true
  );

  document.addEventListener(
    "keydown",
    (e) => {
      if (e.key !== "Escape") return;
      for (const inst of [...__uiPopInstances]) {
        if (!inst?.isOpen?.()) continue;
        inst.close?.();
      }
    },
    true
  );

  const repositionAllOpen = () => {
    for (const inst of [...__uiPopInstances]) {
      if (!inst?.isOpen?.()) continue;
      inst.position?.();
    }
  };

  window.addEventListener("resize", repositionAllOpen, { passive: true });
  window.addEventListener("scroll", repositionAllOpen, { passive: true });
}

function createPopover({ title = "Popover", bodyHTML = "", className = "" } = {}) {
  installPopoverGlobalsOnce();

  const el = document.createElement("div");
  el.className = `ui-popover ${className}`.trim();

  el.innerHTML = `
    <div class="ui-pop-header">
      <div class="ui-pop-title">${title}</div>
      <button type="button" class="ui-pop-close" data-ui-pop-close="1" aria-label="Close" title="Close">
        ${CLOSE_ICON_SVG}
      </button>
    </div>
    <div class="ui-pop-body">${bodyHTML}</div>
  `.trim();

  document.body.appendChild(el);
  patchCloseButtons(el);

  let open = false;
  let anchor = null;
  let destroyed = false;

  function doClose() {
    if (!open) return;
    open = false;
    anchor = null;
    el.classList.remove("ui-open");
  }

  function doOpen(anchorEl) {
    if (destroyed) return;
    anchor = anchorEl || null;
    open = true;
    el.classList.add("ui-open");

    requestAnimationFrame(() => {
      positionPopover(el, anchor);
      requestAnimationFrame(() => positionPopover(el, anchor));
    });
  }

  el.addEventListener("click", (e) => {
    const btn = e.target?.closest?.("[data-ui-pop-close]");
    if (btn) doClose();
  });

  function doDestroy() {
    if (destroyed) return;
    destroyed = true;
    doClose();
    __uiPopInstances.delete(api);
    try {
      el.remove();
    } catch {
      try {
        if (el.parentNode) el.parentNode.removeChild(el);
      } catch {}
    }
  }

  const api = {
    el,
    open: doOpen,
    close: doClose,
    isOpen: () => open,
    position: () => { if (open) positionPopover(el, anchor); },
    destroy: doDestroy,
  };

  __uiPopInstances.add(api);
  return api;
}

function installAutoPatch() {
  patchCloseButtons(document);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => patchCloseButtons(document));
  }

  setTimeout(() => patchCloseButtons(document), 50);
  setTimeout(() => patchCloseButtons(document), 250);

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes || []) {
        if (!node || node.nodeType !== 1) continue;
        patchCloseButtons(node);
      }
    }
  });

  mo.observe(document.documentElement || document.body, { childList: true, subtree: true });
}

installAutoPatch();

export const UiPopovers = Object.freeze({
  create: createPopover,
  position: positionPopover,
  patchCloseButtons: () => patchCloseButtons(document),
});

// Expose for page scripts that run outside module imports.
window.UiPopovers = UiPopovers;

export default UiPopovers;
